import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Globe, CheckCircle2, Clock, ShieldCheck, AlertCircle } from "lucide-react";

const STATE_BADGE = {
    not_requested: { cls: "bg-slate-100 text-slate-700", label: "Not requested", icon: Globe },
    pending: { cls: "bg-yellow-100 text-yellow-900", label: "Pending admin review", icon: Clock },
    verification_required: { cls: "bg-blue-100 text-[#123B5D]", label: "Verification required", icon: AlertCircle },
    verified: { cls: "bg-green-100 text-[#15803D]", label: "Verified", icon: ShieldCheck },
    active: { cls: "bg-green-100 text-[#15803D]", label: "Active", icon: CheckCircle2 },
    rejected: { cls: "bg-red-100 text-[#DC2626]", label: "Rejected", icon: AlertCircle },
};

export default function Domain() {
    const [state, setState] = useState(null);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [doctor, setDoctor] = useState(null);

    async function load() {
        try {
            const r = await api.get("/me/domain");
            setState(r.data);
            if (r.data?.domain) setInput(r.data.domain);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load"));
        }
    }
    useEffect(() => {
        api.get("/me/doctor").then((r) => setDoctor(r.data)).catch(() => {});
        load();
    }, []);

    async function request() {
        setBusy(true);
        try {
            await api.post("/me/domain", { domain: input });
            toast.success("Request submitted");
            load();
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        } finally {
            setBusy(false);
        }
    }

    if (!state) return <DoctorShell><div className="text-[#17212B]/60">Loading…</div></DoctorShell>;
    const badge = STATE_BADGE[state.state] || STATE_BADGE.not_requested;
    const Icon = badge.icon;
    const dns = state.dns_instructions || {};

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6" data-testid="domain-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Presence</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Custom domain</h1>
                <p className="text-[#17212B]/60 text-sm mt-1">Point your own domain to your profile. Actual DNS/SSL verification is coordinated with the platform team.</p>
            </div>

            <Card className="border-[#123B5D]/10 mb-6" data-testid="domain-state-card">
                <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Icon size={14} /> Status</CardTitle></CardHeader>
                <CardContent>
                    <span className={"ddi-badge " + badge.cls} data-testid="domain-state-badge">{badge.label}</span>
                    {state.domain && <div className="mt-2 font-mono text-[#123B5D]" data-testid="domain-name">{state.domain}</div>}
                    {state.admin_notes && <div className="mt-2 text-xs text-[#17212B]/70">Admin note: {state.admin_notes}</div>}
                </CardContent>
            </Card>

            <Card className="border-[#123B5D]/10 mb-6">
                <CardHeader><CardTitle className="text-sm">{state.state === "not_requested" ? "Request a custom domain" : "Change domain"}</CardTitle></CardHeader>
                <CardContent className="space-y-3">
                    <div>
                        <div className="text-xs text-[#17212B]/60 mb-1">Domain</div>
                        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="e.g. drmehta.in" data-testid="domain-input" />
                    </div>
                    <Button onClick={request} disabled={busy || !input} className="bg-[#123B5D] hover:bg-[#0b2740] text-white" data-testid="domain-submit">
                        Submit request
                    </Button>
                </CardContent>
            </Card>

            <Card className="border-[#123B5D]/10">
                <CardHeader><CardTitle className="text-sm">DNS instructions</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                    <div><span className="text-xs text-[#17212B]/60">CNAME target</span>: <code className="bg-[#F5F8FA] px-2 py-0.5 rounded">{dns.cname_target}</code></div>
                    <div><span className="text-xs text-[#17212B]/60">Verification TXT prefix</span>: <code className="bg-[#F5F8FA] px-2 py-0.5 rounded">{dns.verification_txt_prefix}</code></div>
                    <div className="text-xs text-[#17212B]/60 mt-3">{dns.notes}</div>
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
